<template>
	<div class="home-dashboard h-100">
		<b-alert :show="!!error" variant="danger" class="dashboard-error m-2">{{error&&(error.error||error.toString())}}</b-alert>

		<!-- Default (old) layout: shown to everyone by default and always on mobile. Not editable. -->
		<classic-dashboard
			v-if="!showGrid"
			:user="user"
			:restreaming-data="restreamingData"
			:service-icons="serviceIcons"
			:loading="loading"
			:error="error"
			@reload-user="reloadUserData"
			@reload-restreaming="loadUserRestreamingData"
		/>

		<!-- Custom layout: editable drag/resize grid (desktop only). -->
		<div v-else class="home h-100 edit-mode" ref="home">
			<grid-layout
				:layout.sync="layout.grid"
				:col-num="COL_NUM"
				:row-height="ROW_HEIGHT"
				:margin="[8, 8]"
				:is-draggable="true"
				:is-resizable="true"
				:vertical-compact="true"
				:use-css-transforms="true"
				:responsive="false"
			>
				<grid-item
					v-for="item in layout.grid"
					:key="item.i"
					:x="item.x"
					:y="item.y"
					:w="item.w"
					:h="item.h"
					:i="item.i"
					:min-w="minFor(item.i).w"
					:min-h="minFor(item.i).h"
					drag-allow-from=".panel-header"
					drag-ignore-from=".panel-menu, .dropdown-menu, button, a, input, select, textarea"
				>
					<component
						:is="componentFor(item.i)"
						:ref="item.i"
						:edit-mode="true"
						v-bind="panelProps(item.i)"
						v-on="panelListeners(item.i)"
					/>
				</grid-item>
			</grid-layout>
		</div>

		<stream-reset-modal ref="streamResetModal" @saved="loadUserRestreamingData" />
	</div>
</template>

<script>
import { GridLayout, GridItem } from 'vue-grid-layout';
import FetchPoller from '../FetchPoller';
import util from '../util';
import dashboardControls from '../dashboardControls';
import ClassicDashboard from '../components/ClassicDashboard.vue';
import StreamResetModal from '../components/User/Modal/StreamReset.vue';
import PreviewPanel from '../components/panels/PreviewPanel.vue';
import StreamDetailsPanel from '../components/panels/StreamDetailsPanel.vue';
import ChannelsPanel from '../components/panels/ChannelsPanel.vue';
import LogPanel from '../components/panels/LogPanel.vue';
import StatsPanel from '../components/panels/StatsPanel.vue';
import GoalPanel from '../components/panels/GoalPanel.vue';

// The full set of dashboard panels. The id is stable and is what gets persisted to localStorage.
const PANELS = [
	{ id: 'preview', title: 'Preview', component: 'PreviewPanel' },
	{ id: 'streamDetails', title: 'Stream details', component: 'StreamDetailsPanel' },
	{ id: 'channels', title: 'Channels', component: 'ChannelsPanel' },
	{ id: 'chatLog', title: 'Chat log', component: 'LogPanel' },
	{ id: 'alertLog', title: 'Alerts', component: 'LogPanel' },
	{ id: 'stats', title: 'Statistics', component: 'StatsPanel' },
	{ id: 'giftGoal', title: 'Donation goal', component: 'GoalPanel' },
	{ id: 'subGoal', title: 'Membership goal', component: 'GoalPanel' },
	{ id: 'followerGoal', title: 'Follower goal', component: 'GoalPanel' },
];
// Panels that start hidden — users opt in via the panel toggle menu.
const HIDDEN_BY_DEFAULT = ['stats', 'giftGoal', 'subGoal', 'followerGoal'];
const LAYOUT_KEY = 'dashboardLayout';
const COL_NUM = 8;
const ROW_HEIGHT = 30;
const MARGIN_Y = 8;
const STREAM_DETAILS_H = 6; // rows for the stream-details panel in the default grid
const PREVIEW_H = 9;        // rows for the preview panel in the default grid
const MOBILE_QUERY = '(max-width: 991px)';
// Minimum size (grid units) per panel so nothing can be shrunk to an unusable sliver.
const MIN = {
	preview: { w: 2, h: 6 },
	streamDetails: { w: 2, h: 4 },
	channels: { w: 2, h: 6 },
	chatLog: { w: 2, h: 6 },
	alertLog: { w: 2, h: 6 },
	stats: { w: 3, h: 6 },
	giftGoal: { w: 2, h: 4 },
	subGoal: { w: 2, h: 4 },
	followerGoal: { w: 2, h: 4 },
};

export default {
	name: 'Home',
	components: { GridLayout, GridItem, ClassicDashboard, StreamResetModal, PreviewPanel, StreamDetailsPanel, ChannelsPanel, LogPanel, StatsPanel, GoalPanel },
	data() { return {
		loading: 0,
		error: null,
		user: {},
		restreamingData: null,
		restreamingLoading: true,
		services: [],
		serviceIcons: {},
		timezones: ['UTC'],
		categories: [],
		isMobile: window.matchMedia(MOBILE_QUERY).matches,
		// mode: 'default' (old fixed layout) | 'custom' (editable grid); grid: [{i,x,y,w,h}]; hidden: [id]
		layout: { mode: 'default', grid: [], hidden: [] },
		channelsRefresh: 0,
		schedulingAt: null,
		COL_NUM,
		ROW_HEIGHT,
	}},
	computed: {
		// The editable grid is only shown in custom mode and never on mobile.
		showGrid() { return this.layout.mode === 'custom' && !this.isMobile; },
		isPro() { return !!this.user.is_subscribed; },
	},
	watch: {
		layout: { deep: true, handler() { this.saveLayout(); this.publishControls(); } },
		isMobile() { this.publishControls(); },
	},
	created() {
		this.loadLayout();
		dashboardControls.active = true;
		dashboardControls.$on('edit', this.enterCustom);
		dashboardControls.$on('reset', this.resetLayout);
		dashboardControls.$on('toggle-panel', this.togglePanel);
		this.publishControls();
	},
	async mounted() {
		this._mq = window.matchMedia(MOBILE_QUERY);
		this._mqHandler = e => { this.isMobile = e.matches; };
		this._mq.addEventListener('change', this._mqHandler);
		try {
			this.error = null;
			this.loading++;
			this.serviceIcons = {};
			const services = await this.$api.get('services');
			this.services = services.filter(s => s.capabilities.indexOf('chat') != -1);
			for(let service of services)
				if(service.icon_url)
					this.serviceIcons[service.id] = service.icon_url;
			this.restreamingPoller = new FetchPoller(() => this.loadUserRestreamingData(), 5 * 60 * 1000);
			this.restreamingPoller.start();

			await this.loadUserData();

			this.subId = this.$socket.subscribe(this.user.widget_hash, {
				userChanged: () => this.loadUserData(),
				restreamStatus: status => {
					if(status.createdTime) {
						status.createdTime = new Date(status.createdTime);
						status.uptime = this.computeUptime(status.createdTime);
					}
					if(this.restreamingData) this.restreamingData.status = status;
				},
			});

			this.$api.get('timezones').then(timezones => this.timezones = timezones.sort()).catch(e => this.error = e);
			this.$api.get('categories').then(categories => this.categories = categories.results).catch(e => this.error = e);

			this.uptimeInterval = setInterval(() => {
				if(!(this?.restreamingData?.status?.createdTime instanceof Date)) return;
				this.restreamingData.status.uptime = this.computeUptime(this.restreamingData.status.createdTime);
			}, 1000);
		} catch(e) {
			this.error = e;
		} finally {
			this.loading--;
		}
	},
	beforeDestroy() {
		dashboardControls.active = false;
		dashboardControls.canEdit = false;
		dashboardControls.$off('edit', this.enterCustom);
		dashboardControls.$off('reset', this.resetLayout);
		dashboardControls.$off('toggle-panel', this.togglePanel);
		if(this._mq) this._mq.removeEventListener('change', this._mqHandler);
		this.restreamingPoller?.stop();
		if(this.uptimeInterval) clearInterval(this.uptimeInterval);
		this.subId && this.$socket.unsubscribe(this.subId);
	},
	methods: {
		componentFor(id) { return (PANELS.find(p => p.id === id) || {}).component; },
		minFor(id) { return MIN[id] || { w: 2, h: 3 }; },
		// Keep the nav menu in sync with our state.
		publishControls() {
			dashboardControls.mode = this.layout.mode;
			dashboardControls.canEdit = !this.isMobile;
			dashboardControls.panels = PANELS.map(p => ({ id: p.id, title: p.title, visible: this.layout.grid.some(g => g.i === p.id) }));
		},
		togglePanel(id) {
			if(this.layout.grid.some(g => g.i === id)) this.removePanel(id);
			else this.addPanel(id);
		},
		panelProps(id) {
			switch(id) {
				case 'preview': return { restreamingData: this.restreamingData, loading: this.restreamingLoading };
				case 'streamDetails': return { user: this.user, serviceIcons: this.serviceIcons, loading: this.loading, canReset: !!this.restreamingData };
				case 'channels': return { refreshToken: this.channelsRefresh, schedulingAt: this.schedulingAt };
				case 'chatLog': return { mode: 'chat', serviceIcons: this.serviceIcons };
				case 'alertLog': return { mode: 'alerts', serviceIcons: this.serviceIcons };
				case 'giftGoal': return { kind: 'gift' };
				case 'subGoal': return { kind: 'sub' };
				case 'followerGoal': return { kind: 'follower' };
				default: return {};
			}
		},
		panelListeners(id) {
			const base = { remove: () => this.removePanel(id) };
			switch(id) {
				case 'preview': return { ...base, 'reset-stream': this.openStreamReset };
				case 'streamDetails': return {
					...base,
					'reset-stream': this.openStreamReset,
					scheduled: this.onScheduled,
					'refresh-channels': () => { this.channelsRefresh++; },
					updated: this.reloadUserData,
				};
				case 'channels': return { ...base, 'bottom-out': this.onBottomOut };
				default: return base;
			}
		},

		// --- Layout persistence -------------------------------------------------
		loadLayout() {
			let saved = null;
			try { saved = JSON.parse(localStorage.getItem(LAYOUT_KEY) || 'null'); } catch(e) { saved = null; }
			// Only honour a saved custom grid. Everything else (new users, legacy grid-only saves)
			// starts on the default/old layout so nobody perceives a change.
			if(saved && saved.mode === 'custom' && Array.isArray(saved.grid) && saved.grid.length) {
				const rec = this.reconcile(saved.grid, saved.hidden || []);
				this.layout = { mode: 'custom', grid: rec.grid, hidden: rec.hidden };
			} else {
				this.layout = { mode: 'default', grid: [], hidden: [] };
			}
		},
		saveLayout() {
			localStorage.setItem(LAYOUT_KEY, JSON.stringify(this.layout));
		},
		// Switch into the editable grid. Seed the default arrangement only if there's no grid yet
		// (a fresh start / after a reset); otherwise keep whatever the user already arranged.
		enterCustom() {
			if(this.isMobile) return;
			if(!this.layout.grid || !this.layout.grid.length) {
				const def = this.computeDefaultGrid();
				const rec = this.reconcile(def.grid, def.hidden);
				this.$set(this.layout, 'grid', rec.grid);
				this.$set(this.layout, 'hidden', rec.hidden);
			}
			this.layout.mode = 'custom';
		},
		resetLayout() {
			this.layout = { mode: 'default', grid: [], hidden: [] };
		},
		availableHeight() {
			const el = this.$refs.home;
			if(!el) return window.innerHeight - 90;
			const cs = getComputedStyle(el);
			return el.clientHeight - (parseFloat(cs.paddingTop) || 0) - (parseFloat(cs.paddingBottom) || 0);
		},
		rowsForHeight(h) {
			return Math.max(MIN.channels.h, Math.floor((h - MARGIN_Y) / (ROW_HEIGHT + MARGIN_Y)));
		},
		// Default grid arrangement, sized to fill the height and matching the subscription tier.
		computeDefaultGrid() {
			const rows = this.rowsForHeight(this.availableHeight());
			if(!this.isPro) {
				return {
					grid: [
						{ i: 'channels', x: 0, y: 0, w: 4, h: rows },
						{ i: 'chatLog', x: 4, y: 0, w: 4, h: rows },
					],
					hidden: ['preview', 'streamDetails', 'alertLog', ...HIDDEN_BY_DEFAULT],
				};
			}
			const channelsH = Math.max(MIN.channels.h, rows - PREVIEW_H - STREAM_DETAILS_H);
			const leftTotal = PREVIEW_H + STREAM_DETAILS_H + channelsH;
			return {
				grid: [
					{ i: 'preview', x: 0, y: 0, w: 4, h: PREVIEW_H },
					{ i: 'streamDetails', x: 0, y: PREVIEW_H, w: 4, h: STREAM_DETAILS_H },
					{ i: 'channels', x: 0, y: PREVIEW_H + STREAM_DETAILS_H, w: 4, h: channelsH },
					{ i: 'chatLog', x: 4, y: 0, w: 4, h: leftTotal },
				],
				hidden: ['alertLog', ...HIDDEN_BY_DEFAULT],
			};
		},
		// Drop unknown ids, clamp to min sizes, and append newly-registered panels at the bottom.
		reconcile(grid, hidden) {
			const ids = PANELS.map(p => p.id);
			const seen = new Set();
			const outGrid = [];
			for(const g of grid) {
				if(!ids.includes(g.i) || seen.has(g.i)) continue;
				seen.add(g.i);
				const min = this.minFor(g.i);
				outGrid.push({ i: g.i, x: +g.x || 0, y: +g.y || 0, w: Math.max(min.w, +g.w || min.w), h: Math.max(min.h, +g.h || min.h) });
			}
			const outHidden = hidden.filter(id => ids.includes(id) && !seen.has(id) && (seen.add(id), true));
			let y = outGrid.reduce((m, g) => Math.max(m, g.y + g.h), 0);
			for(const p of PANELS)
				if(!seen.has(p.id)) {
					seen.add(p.id);
					if(HIDDEN_BY_DEFAULT.includes(p.id)) { outHidden.push(p.id); continue; }
					const min = this.minFor(p.id);
					outGrid.push({ i: p.id, x: 0, y, w: 4, h: Math.max(min.h, 8) }); y += 8;
				}
			return { grid: outGrid, hidden: outHidden };
		},
		removePanel(id) {
			const idx = this.layout.grid.findIndex(g => g.i === id);
			if(idx !== -1) this.layout.grid.splice(idx, 1);
			if(!this.layout.hidden.includes(id)) this.layout.hidden.push(id);
		},
		addPanel(id) {
			const idx = this.layout.hidden.indexOf(id);
			if(idx !== -1) this.layout.hidden.splice(idx, 1);
			if(this.layout.grid.some(g => g.i === id)) return;
			const y = this.layout.grid.reduce((m, g) => Math.max(m, g.y + g.h), 0);
			const min = this.minFor(id);
			this.layout.grid.push({ i: id, x: 0, y, w: 4, h: Math.max(min.h, 8) });
		},

		// --- Cross-panel coordination ------------------------------------------
		openStreamReset() { this.$refs.streamResetModal.show(); },
		onScheduled(at) { this.schedulingAt = at; this.channelsRefresh++; },
		onBottomOut() {
			const logs = this.$refs.chatLog;
			(Array.isArray(logs) ? logs[0] : logs)?.bottomOut?.();
		},

		// --- Shared data --------------------------------------------------------
		replaceTemplateTags(str, data, date) {
			const d = date ? new Date(date) : new Date();
			const dateStr = isNaN(d) ? '' : d.toLocaleDateString('en-US', { timeZone: data.timezone||'UTC' });
			return (str||'Live with LiveJoiner')
				.replace(/\{episode\}/gi, data.stream_episode_number)
				.replace(/\{date\}/gi, dateStr);
		},
		computeUptime(date) {
			return util.secondsToTime(Math.round(((new Date()).getTime() - date.getTime()) / 1000.0));
		},
		async loadUserData() {
			this.user = await this.$api.get('userData');
			this.user.stream_title = this.replaceTemplateTags(this.user.stream_title_template, this.user);
		},
		async reloadUserData() {
			try {
				this.error = null;
				this.loading++;
				await this.loadUserData();
			} catch(e) {
				console.error(e);
				this.error = e;
			} finally {
				this.loading--;
			}
		},
		loadUserRestreamingData() {
			this.restreamingLoading = true;
			return this.$api.get('userRestreamingData').then(data => {
				if(data.status.createdTime) {
					data.status.createdTime = new Date(data.status.createdTime);
					data.status.uptime = this.computeUptime(data.status.createdTime);
				}
				this.restreamingData = data;
			}, () => {
				this.restreamingData = null;
			}).finally(() => {
				this.restreamingLoading = false;
			});
		},
	},
};
</script>

<style>
.home-dashboard {
	position: relative;
}

/* --- Custom editable grid --- */
.home {
	overflow-y: auto;
	overflow-x: hidden;
	/* No padding: vue-grid-layout already reserves 8px edge gutters via its margin; padding here causes a phantom scrollbar. */
	padding: 0;
}
.home .vue-grid-item {
	overflow: visible;
}
/* Only the drag handle (panel header, styled in DashboardPanel) and the resize handle
   suppress text selection while dragging; both set user-select: none on their own rules.
   Panel body content stays selectable for copy/paste. */
.home .vue-grid-item > .dashboard-panel {
	height: 100%;
}
/* Raise a panel above its neighbours while its ⋮ menu is open so the menu isn't covered
   (grid items use CSS transforms, which create stacking contexts). */
.home .vue-grid-item:has(.dropdown.show) {
	z-index: 20;
}
/* Grab / grabbing cursor on the title bar. !important is required because vue-grid-layout
   (interact.js) sets `cursor: move` as an inline style on the drag handle. */
.home .vue-grid-item .panel-header,
.home .vue-grid-item .panel-header .panel-title,
.home .vue-grid-item .panel-header .panel-header-side {
	cursor: grab !important;
}
.home .vue-grid-item.vue-draggable-dragging .panel-header,
.home .vue-grid-item.vue-draggable-dragging .panel-header .panel-title,
.home .vue-grid-item.vue-draggable-dragging .panel-header .panel-header-side {
	cursor: grabbing !important;
}
.home .vue-grid-item .panel-header .panel-menu-btn,
.home .vue-grid-item .panel-header .panel-menu-btn * {
	cursor: pointer !important;
}
.home .vue-grid-item.vue-grid-placeholder {
	background: var(--primary, #5df286);
	opacity: .2;
	border-radius: .5rem;
}
/* Make the bottom-right resize handle clearly visible (the library's default is a faint arrow). */
.home .vue-grid-item > .vue-resizable-handle {
	width: 22px;
	height: 22px;
	right: 0;
	bottom: 0;
	padding: 0;
	background: none;
	opacity: .6;
	transition: opacity .15s ease;
	user-select: none;
	-webkit-user-select: none;
}
.home .vue-grid-item > .vue-resizable-handle::after {
	content: '';
	position: absolute;
	right: 1px;
	bottom: 1px;
	width: 11px;
	height: 11px;
	border-right: 3px solid var(--primary, #5df286);
	border-bottom: 3px solid var(--primary, #5df286);
	border-bottom-right-radius: 3px;
}
.home .vue-grid-item:hover > .vue-resizable-handle {
	opacity: 1;
}

/* Errors overlay the top of the grid instead of pushing it down (keeps panels full height). */
.dashboard-error {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	z-index: 1010;
}

input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
	-webkit-appearance: none;
	margin: 0;
}

/* Firefox */
input[type=number] {
	-moz-appearance: textfield;
}
</style>
